/**
 * CR-5972: Grouped bulk action bar for the PO Lines table.
 *
 * When the supplier or customer selects lines with a single Ack Status, the
 * bar renders flat — one row of action buttons matching that status. When
 * they select a mix (e.g. ``New`` + ``Changed``), the bar splits into one
 * labelled section per status, with each section's actions wired to the
 * subset of selected lines in that status. This prevents mixed-status
 * selections from offering inapplicable actions (e.g. "Accept All" on a
 * ``New`` line, where the correct action is "Acknowledge").
 *
 * Action mapping per status (mirrors per-line action visibility):
 *   Changed: Put On Hold | Accept | Reject
 *   New:     Acknowledge | Propose Change | Put On Hold (+ Reject if
 *            ``allow_supplier_decline`` is enabled)
 *   WIP:     Put On Hold | Propose Change
 *
 * The ``allow_supplier_decline`` feature flag is off for all clients today,
 * so the ``new``-status Reject button stays hidden. Toggling the flag will
 * surface it without a code change.
 */
import { Button } from '../../../ui/button';
import { Check, X, Pause, Edit, Loader2 } from 'lucide-react';
import { PurchaseOrderLine } from './types';
import { useMenuSettings } from '../../../../contexts/MenuSettingsContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { isChangeProposedStatus, canActOnProposal } from '../../pendingProposal';

interface BulkActionBarProps {
  selectedLines: PurchaseOrderLine[];
  bulkAccepting: boolean;
  bulkRejecting: boolean;
  bulkHolding: boolean;
  bulkProposing: boolean;
  onClear: () => void;
  onAccept: (ids: Set<number>, verb: 'accept' | 'acknowledge') => void;
  onReject: (ids: Set<number>) => void;
  onHold: (ids: Set<number>) => void;
  onPropose: (ids: Set<number>) => void;
}

type GroupKey = 'changed' | 'new' | 'wip' | 'other';
type ActionKey = 'hold' | 'accept' | 'reject' | 'acknowledge' | 'propose';

interface GroupConfig {
  key: GroupKey;
  label: string;
  badgeClass: string;
  // Order matters — controls left-to-right rendering inside the group.
  actions: ActionKey[];
}

const GROUP_CONFIG: Record<GroupKey, Omit<GroupConfig, 'key'>> = {
  changed: {
    label: 'Changed',
    badgeClass: 'bg-amber-500 text-white',
    actions: ['hold', 'accept', 'reject'],
  },
  new: {
    // Default actions; ``reject`` only added when the decline flag is on.
    label: 'New',
    badgeClass: 'bg-blue-500 text-white',
    actions: ['acknowledge', 'propose', 'hold'],
  },
  wip: {
    label: 'WIP',
    badgeClass: 'bg-green-500 text-white',
    actions: ['hold', 'propose'],
  },
  other: {
    // Fallback for any status we didn't enumerate (declined, despatched, etc.).
    // Only Hold is universally valid, so that's the only action we surface.
    label: 'Other',
    badgeClass: 'bg-gray-400 text-white',
    actions: ['hold'],
  },
};

/**
 * Which bulk-action group a line belongs to. Exported for test: CR-7277 turns
 * on a line landing in "other" rather than "changed", and asserting that
 * directly is clearer than inferring it from rendered buttons.
 */
export function groupKeyFor(line: PurchaseOrderLine): GroupKey {
  // CR-7277: a line whose status claims a pending change but which carries no
  // supplier_proposed_* value has nothing to accept. Bucketing it as "changed"
  // put Accept and Reject on the BULK bar for it -- the worst version of this
  // defect, since one click covers every selected line and accepting writes
  // supplier_promise_date over the agreed date. It belongs in "other", where
  // only Hold is offered.
  if (isChangeProposedStatus(line.acknowledge_status) && !canActOnProposal(line)) {
    return 'other';
  }
  switch (line.acknowledge_status) {
    case 'new':
      return 'new';
    case 'change_proposed_supplier':
    case 'change_proposed_customer':
      return 'changed';
    case 'wip':
    case 'accepted':
      return 'wip';
    default:
      return 'other';
  }
}

interface BuiltGroup {
  key: GroupKey;
  label: string;
  badgeClass: string;
  actions: ActionKey[];
  lineIds: Set<number>;
}

export function BulkActionBar({
  selectedLines,
  bulkAccepting,
  bulkRejecting,
  bulkHolding,
  bulkProposing,
  onClear,
  onAccept,
  onReject,
  onHold,
  onPropose,
}: BulkActionBarProps) {
  const { isFeatureEnabled } = useMenuSettings();
  const { user } = useAuth();
  const isSupplier = user?.platform === 'SUPPLIER';
  // Decline-for-new is supplier-side only and gated by the global feature flag.
  // When the flag is off (the default for all clients), New lines get
  // Acknowledge / Propose / Hold without a Reject — matching the per-line UI.
  const allowNewReject = isSupplier && isFeatureEnabled('allow_supplier_decline');

  const totalSelected = selectedLines.length;
  const bulkMode = totalSelected >= 2;

  // Build groups in a stable order so the bar layout doesn't reshuffle as
  // selections change. Changed (action-needed) first, then New, then WIP.
  const groupOrder: GroupKey[] = ['changed', 'new', 'wip', 'other'];
  const groups: BuiltGroup[] = [];
  for (const key of groupOrder) {
    const ids = new Set(
      selectedLines.filter(l => groupKeyFor(l) === key).map(l => l.id)
    );
    if (ids.size === 0) continue;
    const cfg = GROUP_CONFIG[key];
    let actions = [...cfg.actions];
    if (key === 'new' && allowNewReject) {
      // Slot Reject just after Acknowledge so it lives where users expect.
      actions = ['acknowledge', 'reject', 'propose', 'hold'];
    }
    groups.push({ key, label: cfg.label, badgeClass: cfg.badgeClass, actions, lineIds: ids });
  }

  const renderAction = (
    action: ActionKey,
    ids: Set<number>,
    groupLabel: string,
  ) => {
    const count = ids.size;
    const disabled = count === 0;
    switch (action) {
      case 'hold':
        return (
          <Button
            key={`${groupLabel}-hold`}
            size="sm"
            variant="outline"
            onClick={() => onHold(ids)}
            disabled={disabled || bulkHolding}
            className={!disabled ? 'border-orange-500 text-orange-700 hover:bg-orange-50' : 'opacity-60 cursor-not-allowed'}
          >
            {bulkHolding ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Holding...</> : <><Pause className="h-4 w-4 mr-2" />Put On Hold</>}
          </Button>
        );
      case 'accept':
        return (
          <Button
            key={`${groupLabel}-accept`}
            size="sm"
            onClick={() => onAccept(ids, 'accept')}
            disabled={disabled || bulkAccepting}
            className={!disabled ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-green-200 text-green-600 cursor-not-allowed opacity-60'}
          >
            {bulkAccepting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Accepting...</> : <><Check className="h-4 w-4 mr-2" />Accept</>}
          </Button>
        );
      case 'acknowledge':
        return (
          <Button
            key={`${groupLabel}-acknowledge`}
            size="sm"
            onClick={() => onAccept(ids, 'acknowledge')}
            disabled={disabled || bulkAccepting}
            className={!disabled ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-green-200 text-green-600 cursor-not-allowed opacity-60'}
          >
            {bulkAccepting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Acknowledging...</> : <><Check className="h-4 w-4 mr-2" />Acknowledge</>}
          </Button>
        );
      case 'reject':
        return (
          <Button
            key={`${groupLabel}-reject`}
            size="sm"
            onClick={() => onReject(ids)}
            disabled={disabled || bulkRejecting}
            className={!disabled ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-red-200 text-red-400 cursor-not-allowed opacity-60'}
          >
            {bulkRejecting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Rejecting...</> : <><X className="h-4 w-4 mr-2" />Reject</>}
          </Button>
        );
      case 'propose':
        return (
          <Button
            key={`${groupLabel}-propose`}
            size="sm"
            variant="outline"
            onClick={() => onPropose(ids)}
            disabled={disabled || bulkProposing}
            className={!disabled ? 'border-blue-500 text-blue-700 hover:bg-blue-50' : 'opacity-60 cursor-not-allowed'}
          >
            {bulkProposing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Proposing...</> : <><Edit className="h-4 w-4 mr-2" />Propose Change</>}
          </Button>
        );
      default:
        return null;
    }
  };

  const renderHeader = () => (
    <div className="flex items-center gap-2 flex-wrap">
      <span className={`text-sm ${bulkMode ? 'text-amber-900 font-medium' : 'text-muted-foreground'}`}>
        {totalSelected > 0 ? `${totalSelected} line${totalSelected > 1 ? 's' : ''} selected` : 'No lines selected'}
      </span>
      {bulkMode && (
        <span className="text-xs text-amber-800">
          — bulk mode: per-line actions disabled. Use the buttons here, or clear the selection.
        </span>
      )}
      {totalSelected > 0 && (
        <button
          onClick={onClear}
          className="text-xs text-muted-foreground underline hover:text-foreground"
        >
          Clear
        </button>
      )}
    </div>
  );

  // Empty state — render placeholder so layout doesn't jump when nothing's
  // selected. Mirror the original disabled-buttons look.
  if (totalSelected === 0) {
    return (
      <div className="mb-4 p-3 border rounded-lg flex items-center gap-3 bg-muted/40 border-border">
        {renderHeader()}
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled className="opacity-60 cursor-not-allowed">
            <Pause className="h-4 w-4 mr-2" />Put On Hold All
          </Button>
          <Button size="sm" disabled className="bg-green-200 text-green-600 cursor-not-allowed opacity-60">
            <Check className="h-4 w-4 mr-2" />Accept All
          </Button>
          <Button size="sm" disabled className="bg-red-200 text-red-400 cursor-not-allowed opacity-60">
            <X className="h-4 w-4 mr-2" />Reject All
          </Button>
        </div>
      </div>
    );
  }

  // Single-group selection — keep the original flat layout but use group-aware
  // actions (e.g. "Acknowledge" for an all-New selection, not "Accept").
  if (groups.length === 1) {
    const group = groups[0];
    return (
      <div className="mb-4 p-3 border rounded-lg flex items-center gap-3 bg-amber-50 border-amber-300 flex-wrap">
        {renderHeader()}
        <div className="flex items-center gap-2 flex-wrap">
          {group.actions.map(action => renderAction(action, group.lineIds, group.label))}
        </div>
      </div>
    );
  }

  // Mixed selection — render one section per status group.
  return (
    <div className="mb-4 p-3 border rounded-lg bg-amber-50 border-amber-300 space-y-2">
      {renderHeader()}
      <div className="flex flex-col gap-2">
        {groups.map(group => (
          <div
            key={group.key}
            className="flex items-center gap-3 flex-wrap py-1.5 px-2 rounded bg-white/60 border border-amber-200"
          >
            <div className="flex items-center gap-2 min-w-[170px]">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${group.badgeClass}`}>
                {group.label}
              </span>
              <span className="text-xs text-amber-900 font-medium">
                ({group.lineIds.size} line{group.lineIds.size > 1 ? 's' : ''})
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {group.actions.map(action => renderAction(action, group.lineIds, group.label))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
